import { type FC, useState, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import FilterIcon from 'dibs-icons/exports/legacy/Filter.ts';
import { Header } from 'dibs-elements/exports/Header.tsx';
import { NftMarketMobileFacetSelect } from './NftMarketMobileFacetSelect.tsx';

import { messages } from './filterConfig.ts';
import {
    type FilterShape,
    type FilterConfig,
    type NftItemSearchAuctionStatus,
    type NftItemSearchAuctionMarketType,
    type NftItemSearchMediumType,
    type NftItemSearchSort,
} from './filterConfig.ts';

import classnames from 'classnames';
import styles from './NftMarketFiltersMobile.scss';

type Props = {
    state: FilterShape;
    filterConfig: FilterConfig;
    onFiltersChange: (newFilters: FilterShape) => void;
    totalResults: number;
    activeFilterCount: number;
};

export const NftMarketFiltersMobile: FC<Props> = ({
    state,
    filterConfig,
    onFiltersChange,
    totalResults,
    activeFilterCount,
}) => {
    const { formatMessage } = useIntl();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.buttonContainer}>
                <div className={styles.resultCount}>
                    <FormattedMessage
                        id="nft.market.filters.mobile.filterAndSort.resultCount"
                        defaultMessage="{totalResults} results"
                        values={{ totalResults }}
                    />
                </div>

                <Button
                    className={classnames(styles.button, styles.important)}
                    size="medium"
                    type="textCta"
                    onClick={() => setIsModalOpen(true)}
                >
                    <FilterIcon className={styles.filterIcon} />
                    <FormattedMessage
                        id="nft.market.filters.mobile.filterAndSort"
                        defaultMessage="Filter & Sort"
                    />
                    {activeFilterCount > 0 ? ` (${activeFilterCount})` : null}
                </Button>
            </div>

            <ModalContainer isOpen={isModalOpen} onClose={closeModal}>
                <div className={styles.headerContainer}>
                    <div>
                        <Header
                            title={
                                <FormattedMessage
                                    id="nft.market.filters.mobile.filterAndSort.modal.header"
                                    defaultMessage="Filter & Sort"
                                />
                            }
                            size="medium"
                        />
                        <div className={styles.modalResultCount}>
                            <FormattedMessage
                                id="nft.market.filters.mobile.filterAndSort.modal.resultCount"
                                defaultMessage="{totalResults} results"
                                values={{ totalResults }}
                            />
                        </div>
                    </div>
                    <Button size="medium" className={styles.closeButton} onClick={closeModal}>
                        <FormattedMessage
                            id="nft.market.filters.mobile.filterAndSort.closeCta"
                            defaultMessage="Close"
                        />
                    </Button>
                </div>

                <NftMarketMobileFacetSelect
                    isMultiSelect={false}
                    value={state.sortBy}
                    label={formatMessage(messages.sortBy)}
                    onChange={newValue =>
                        onFiltersChange({ ...state, sortBy: newValue as NftItemSearchSort })
                    }
                    options={filterConfig.sortBy.options}
                    dataTn="sort-by"
                />

                <NftMarketMobileFacetSelect
                    isMultiSelect={false}
                    value={state.auctionStatus}
                    label={formatMessage(messages.availability)}
                    onChange={newValue =>
                        onFiltersChange({
                            ...state,
                            auctionStatus: newValue as NftItemSearchAuctionStatus,
                        })
                    }
                    options={filterConfig.auctionStatus.options}
                    dataTn="auction-status"
                />

                <NftMarketMobileFacetSelect
                    isMultiSelect={false}
                    value={state.marketType}
                    label={formatMessage(messages.market)}
                    onChange={newValue =>
                        onFiltersChange({
                            ...state,
                            marketType: newValue as NftItemSearchAuctionMarketType,
                        })
                    }
                    options={filterConfig.marketType.options}
                    dataTn="market-type"
                />

                <NftMarketMobileFacetSelect
                    isMultiSelect
                    values={state.mediumTypes ?? []}
                    label={formatMessage(messages.medium)}
                    onChange={newValue =>
                        onFiltersChange({
                            ...state,
                            mediumTypes: newValue as NftItemSearchMediumType[],
                        })
                    }
                    options={filterConfig.mediumTypes.options}
                    dataTn="medium-type"
                />
            </ModalContainer>
        </div>
    );
};
